// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngCarousel, { SwiperInstance, SwiperStyle } from "@eisinc/App/Components/XngCarousel";
import ProductCarouselProduct, {
    ProductCarouselProductStyles,
} from "@eisinc/Overrides/Components/ProductCarouselProduct";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getProductIdsForCompare,
    getProductsForCompare,
} from "@insite/client-framework/Store/Components/CompareProductsDrawer/CompareProductsDrawerSelectors";
import removeProductIdToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/RemoveProductFromCompare";
import translate from "@insite/client-framework/Translate";
import { AttributeTypeModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import X from "@insite/mobius/Icons/X";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { resolveColor } from "@insite/mobius/utilities";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { createRef, ReactNode, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    id: string;
    extendedStyles?: CarouselStyles;
    title: string;
    hideTitle?: boolean;
    maxNumberOfColumns: number;
    slideDetails: ProductCarouselSlideDetails;
    // XNG-Changes
    showNavigation?: boolean;
}
const mapStateToProps = (state: ApplicationState) => ({
    productIds: getProductIdsForCompare(state),
    products: getProductsForCompare(state),
});

const mapDispatchToProps = {
    removeProductIdToCompare,
};

type Props = OwnProps &
    ThemeProps<BaseTheme> &
    HasShellContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;
export interface ProductCarouselSlideDetails {
    slides: CarouselSlide[];
    products: ProductModel[];
    attributeTypes: AttributeTypeModel[];
}

export interface CarouselSlide {
    id: string;
    renderComponent: (options: CarouselSlideOptions) => ReactNode;
}

export interface CarouselSlideOptions {}

export interface CarouselStyles {
    titleText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselContentEmptyCellGridItem?: GridItemProps;
    carouselCellGridItem?: GridItemProps;
    carouselContentContainer?: GridContainerProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    attributeLabelsCellGridItem?: GridItemProps;
    attributeLabelsGridContainer?: GridContainerProps;
    attributeLabelGridItem?: GridItemProps;
    attributeLabelText?: TypographyPresentationProps;
    attributeValuesCellGridItem?: GridItemProps;
    attributeValuesWrapper?: InjectableCss;
    attributeValuesContainer?: InjectableCss;
    attributeValuesSlidesContainer?: InjectableCss;
    attributeValueSlide?: InjectableCss;
    attributeValueSlideInner?: InjectableCss;
    attributeValuesGridContainer?: GridContainerProps;
    attributeValueGridItem?: GridItemProps;
    attributeValueText?: TypographyPresentationProps;
    // XNG-Changes
    attributeWrapper?: InjectableCss;
    attributeContainer?: InjectableCss;
    attributeLabel?: TypographyProps;
    attributeValue?: TypographyProps;
    attributeCellsGridItem?: GridItemProps;
    emptyValueStyles?: InjectableCss;
    xngCarouselStyles?: SwiperStyle;
    productCard?: ProductCarouselProductStyles;
}

export const carouselStyles: CarouselStyles = {
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: 10px;
        `,
    },
    mainContainer: {
        gap: 0,
        css: css`
            margin: 0 auto;
        `,
    },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-start;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                ])}
        `,
    },
    carouselContentContainer: {
        gap: 0,
    },
    carouselContentEmptyCellGridItem: {
        width: 4,
    },
    carouselCellGridItem: {
        width: 8,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 0 20px;
            display: flex;
            flex-direction: column;
        `,
    },
    attributeLabelsCellGridItem: {
        width: 4,
    },
    attributeLabelsGridContainer: {
        gap: 0,
        css: css`
            z-index: 0;
        `,
    },
    attributeLabelGridItem: {
        width: 12,
        css: css`
            border-bottom: ${({ theme }) => resolveColor("common.border", theme)} solid 1px;
            border-right: ${({ theme }) => resolveColor("common.border", theme)} solid 1px;
            padding: 0.8rem 0.3rem;
            &:nth-child(even) {
                background-color: ${getColor("common.accent")};
            }
        `,
    },
    attributeLabelText: {
        ellipsis: true,
    },
    attributeValuesCellGridItem: {
        width: 8,
    },
    attributeValuesWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    attributeValuesContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    attributeValuesSlidesContainer: {
        css: css`
            display: flex;
        `,
    },
    attributeValueSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
        `,
    },
    attributeValueSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 0;
            display: flex;
            flex-direction: column;
        `,
    },
    attributeValuesGridContainer: {
        gap: 0,
    },
    attributeValueGridItem: {
        width: 12,
        css: css`
            border-bottom-style: solid;
            border-bottom-width: 1px;
            border-bottom-color: ${({ theme }) => resolveColor("common.border", theme)};
            padding: 0.8rem 0.8rem;
            &:nth-child(even) {
                background-color: ${getColor("common.accent")};
            }
        `,
    },
    attributeValueText: {
        ellipsis: true,
    },
};
const productCarouselStyle: ProductCarouselProductStyles = {};

const Carousel = ({
    shellContext,
    theme,
    id,
    extendedStyles,
    title,
    hideTitle,
    maxNumberOfColumns,
    slideDetails,
    removeProductIdToCompare,
    products,
    showNavigation = true,
}: Props) => {
    const afterCarousel = createRef<HTMLSpanElement>();
    const [styles] = useState(() => mergeToNew(carouselStyles, extendedStyles));

    // XNG-Changes
    const [attributeSwipers, setAttributeSwipers] = useState<SwiperInstance[] | undefined>(undefined);
    const [productSwiper, setProductSwiper] = useState<SwiperInstance | undefined>(undefined);

    const breakpoints = {
        // when window width is >= 640px
        0: {
            slidesPerView: 1,
        },
        // when window width is >= 768px
        700: {
            slidesPerView: 2,
        },
        // when window width is >= 768px
        1100: {
            slidesPerView: 3,
        },
        // when window width is >= 768px
        1439: {
            slidesPerView: 4,
        },
    };
    /**
    const [emblaRef, embla] = useEmblaCarousel();
    const [emblaSecondRef, emblaSecond] = useEmblaCarousel();
    const [windowResizeTime, setWindowResizeTime] = useState(0);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const setCanScroll = () => {
        if (shellContext.isInShell) {
            return;
        }
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    const getSlidesToScroll = useCallback(() => {
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }

        let localSlidesToScroll: number;
        const { innerWidth } = window;
        if (innerWidth < theme.breakpoints.values[1]) {
            localSlidesToScroll = 1;
        } else if (innerWidth < theme.breakpoints.values[2]) {
            localSlidesToScroll = 1;
        } else if (innerWidth < theme.breakpoints.values[3]) {
            localSlidesToScroll = 2;
        } else {
            localSlidesToScroll = 3;
        }
        return Math.min(localSlidesToScroll, maxNumberOfColumns);
    }, [maxNumberOfColumns]);

    const [slidesToScroll, setSlidesToScroll] = useState(0);

    useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);
    }, [maxNumberOfColumns, windowResizeTime]);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla]);

    useEffect(() => {
        if (!emblaSecond) {
            return;
        }

        emblaSecond.on("init", setCanScroll);
        emblaSecond.on("select", setCanScroll);
    }, [emblaSecond]);

    useEffect(() => {
        if (!embla || !emblaSecond || !slideDetails || slideDetails.slides.length === 0) {
            return;
        }

        const draggable = isTouchDevice();
        embla.reInit({
            align: "start",
            slidesToScroll,
            watchDrag: draggable,
            loop: true,
        });
        emblaSecond.reInit({
            align: "start",
            slidesToScroll,
            watchDrag: draggable,
            loop: true,
        });
        setCanScroll();

        const onMainPointerUp = () => {
            emblaSecond.scrollTo(embla.selectedScrollSnap(), true);
        };
        const onSecondPointerUp = () => {
            embla.scrollTo(emblaSecond.selectedScrollSnap(), true);
        };

        embla.on("pointerUp", onMainPointerUp);
        emblaSecond.on("pointerUp", onSecondPointerUp);

        return () => {
            embla.off("pointerUp", onMainPointerUp);
            emblaSecond.off("pointerUp", onSecondPointerUp);
            };
            }, [embla, emblaSecond, slidesToScroll, slideDetails]);
    */

    const getProductAttributeValue = (product: ProductModel, attribute: AttributeTypeModel) => {
        const productAttributeValue = product.attributeTypes
            ?.filter(a => a.id === attribute.id)
            .map(a => a.attributeValues || [])
            .reduce((acc, currentValue) => [...acc, ...currentValue], []);

        if (productAttributeValue && productAttributeValue.length > 0) {
            return (
                <Typography
                    aria-describedby={`carousel-${id}_attribute-label-${attribute.id}`}
                    {...styles.attributeValueText}
                >
                    {productAttributeValue.map(o => o.valueDisplay).join(", ")}
                </Typography>
            );
        }

        return <>&nbsp;</>;
    };

    if (!slideDetails || slideDetails.slides.length === 0) {
        return null;
    }

    // XNG-Changes: restructure use XngCarousel
    return (
        <>
            <SkipNav
                text={translate("Skip Carousel")}
                extendedStyles={styles.skipCarouselButton}
                destination={afterCarousel}
            />
            {hideTitle ? (
                <VisuallyHidden>
                    <Typography {...styles.titleText}>{title}</Typography>
                </VisuallyHidden>
            ) : (
                <Typography {...styles.titleText}>{title}</Typography>
            )}

            <GridContainer {...styles.mainContainer} data-test-selector="productCompareCarousel">
                <GridItem {...styles.carouselCellGridItem}>
                    <XngCarousel
                        controller={{ control: attributeSwipers, by: "container" }}
                        onSwiper={setProductSwiper}
                        onBeforeDestroy={setProductSwiper}
                        scrollbar={true}
                        spaceBetween={24}
                        extendedStyles={styles.xngCarouselStyles}
                        draggable={IS_SERVER_SIDE ? false : isTouchDevice()}
                        navigation={showNavigation}
                        id={id}
                        breakpoints={breakpoints}
                    >
                        {products.map((product: ProductModel, index: any) => (
                            <ProductCarouselProduct
                                key={index}
                                carouselId={id}
                                product={product}
                                showImage={true}
                                showBrand={true}
                                showTitle={true}
                                showPartNumbers={true}
                                showPrice={true}
                                showAddToCart={true}
                                showAddToList={true}
                                view="grid"
                                showAvailability={true}
                                extendedStyles={styles.productCard}
                                actionsTemplate={(product: ProductModel) => {
                                    return (
                                        <Clickable
                                            onClick={() => removeProductIdToCompare({ productId: product.id })}
                                            data-test-selector="productCompareRemoveProductButton"
                                            data-test-key={product.id}
                                        >
                                            <VisuallyHidden>
                                                {translate("Remove Product {0} from Compare", product.productTitle)}
                                            </VisuallyHidden>
                                            <Icon size={18} src={X} />
                                        </Clickable>
                                    );
                                }}
                            />
                        ))}
                    </XngCarousel>
                </GridItem>
                <GridItem {...styles.attributeCellsGridItem}>
                    <StyledWrapper {...styles.attributeWrapper}>
                        {slideDetails.attributeTypes.map((attribute, index) => (
                            <StyledWrapper key={attribute.id} {...styles.attributeContainer}>
                                <Typography {...styles.attributeLabel} className={`${index === 0 ? "first-item" : ""}`}>
                                    {attribute.label || attribute.name}
                                </Typography>
                                <XngCarousel
                                    spaceBetween={24}
                                    extendedStyles={styles.xngCarouselStyles}
                                    draggable={IS_SERVER_SIDE ? false : isTouchDevice()}
                                    id={id}
                                    breakpoints={breakpoints}
                                    onSwiper={(swiper: any) =>
                                        setAttributeSwipers(prevState => [...(prevState || []), swiper])
                                    }
                                    onBeforeDestroy={(swiper: any) => {
                                        setAttributeSwipers(prevState => [...(prevState || []), swiper]);
                                        if (productSwiper && !(productSwiper as any).destroyed) {
                                            (productSwiper as any).controller.control = undefined;
                                        }
                                    }}
                                    controller={{ control: productSwiper, by: "container" }}
                                >
                                    {slideDetails.products.map((product, index) => (
                                        <StyledWrapper key={`attribute_value_for_${product.id}`}>
                                            <Typography {...styles.attributeValue}>
                                                {getProductAttributeValue(product, attribute)}
                                            </Typography>
                                        </StyledWrapper>
                                    ))}
                                </XngCarousel>
                            </StyledWrapper>
                        ))}
                    </StyledWrapper>
                </GridItem>
            </GridContainer>
            <span ref={afterCarousel} tabIndex={-1} />
        </>
    );
};

Carousel.defaultProps = {
    maxNumberOfColumns: 4,
};

export default connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withTheme(Carousel)));
